import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const SeverityPanel = ({ severityData }) => {
  const navigate = useNavigate();

  const severityLevels = [
    {
      level: 'critical',
      label: 'Critical',
      count: severityData?.critical,
      icon: 'AlertTriangle',
      color: 'text-error',
      bgColor: 'bg-error/10',
      borderColor: 'border-error/20',
      description: 'Immediate attention required'
    },
    {
      level: 'high',
      label: 'High',
      count: severityData?.high,
      icon: 'AlertCircle',
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      borderColor: 'border-warning/20',
      description: 'Urgent response needed'
    },
    {
      level: 'medium',
      label: 'Medium',
      count: severityData?.medium,
      icon: 'Info',
      color: 'text-accent',
      bgColor: 'bg-accent/10',
      borderColor: 'border-accent/20',
      description: 'Standard priority'
    },
    {
      level: 'low',
      label: 'Low',
      count: severityData?.low,
      icon: 'Minus',
      color: 'text-success',
      bgColor: 'bg-success/10',
      borderColor: 'border-success/20',
      description: 'Low priority monitoring'
    }
  ];

  const handleSeverityClick = (level) => {
    navigate(`/incidents-list?severity=${level}`);
  };

  const getTotalIncidents = () => {
    return severityData?.critical + severityData?.high + severityData?.medium + severityData?.low;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-light">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">Incident Severity</h3>
          <p className="text-sm text-muted-foreground">
            Total: {getTotalIncidents()} active incidents
          </p>
        </div>
        <button
          onClick={() => navigate('/incidents-list')}
          className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-primary hover:bg-primary/10 rounded-md transition-colors duration-200"
        >
          <span>View All</span>
          <Icon name="ArrowRight" size={16} />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {severityLevels?.map((severity) => (
          <button
            key={severity?.level}
            onClick={() => handleSeverityClick(severity?.level)}
            className={`p-4 rounded-lg border-2 ${severity?.borderColor} ${severity?.bgColor} hover:scale-105 transition-all duration-200 text-left group`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className={`p-2 rounded-lg bg-card ${severity?.color}`}>
                <Icon name={severity?.icon} size={20} />
              </div>
              <div className="text-right">
                <div className={`text-2xl font-bold ${severity?.color}`}>
                  {severity?.count}
                </div>
              </div>
            </div>
            
            <div className="space-y-1">
              <h4 className={`font-semibold ${severity?.color}`}>
                {severity?.label}
              </h4>
              <p className="text-xs text-muted-foreground">
                {severity?.description}
              </p>
            </div>

            {severity?.count > 0 && (
              <div className="mt-3 pt-3 border-t border-border/50">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Click to filter</span>
                  <Icon name="Filter" size={12} className="text-muted-foreground group-hover:text-foreground transition-colors duration-200" />
                </div>
              </div>
            )}
          </button>
        ))}
      </div>
      {/* Quick Stats */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-lg font-semibold text-error">
              {Math.round((severityData?.critical / getTotalIncidents()) * 100) || 0}%
            </div>
            <div className="text-xs text-muted-foreground">Critical</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-warning">
              {Math.round((severityData?.high / getTotalIncidents()) * 100) || 0}%
            </div>
            <div className="text-xs text-muted-foreground">High</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-accent">
              {Math.round((severityData?.medium / getTotalIncidents()) * 100) || 0}%
            </div>
            <div className="text-xs text-muted-foreground">Medium</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-success">
              {Math.round((severityData?.low / getTotalIncidents()) * 100) || 0}%
            </div>
            <div className="text-xs text-muted-foreground">Low</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeverityPanel;